import React, { useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Tooltip from '@mui/material/Tooltip';
import { SettingsIcon } from 'assets/header/SettingsIcon';
import UserSvg from 'assets/userContainer.svg';
import Button from 'common-ui/button';
import { Table } from 'common-ui/table';
import { getLeadById, getLeadsPool } from 'features/newsales/newsalesActions';
import {
  selectLeadsPool,
  selectLeadsPoolLoading,
  selectLeadsPoolPagesCount,
  selectLeadsPoolTotalConverted,
  selectLeadsPoolTotalOrdersSum,
} from 'features/newsales/newsalesSlice';
import OrderSelectModal from 'pages/sales/leadInfo/quickActions/orderSelectModal/OrderSelectModal';
import { formatDateTime } from 'utils/dateUtils';

import AssignLeadModal from '../components/assignLeadModal';
import TakeLeadModal from '../components/takeLeadModal/TakeLeadModal';
import {
  AssigneeWrapper,
  Container,
  Header,
  HoveredText,
  Icon,
  LeadIdBadge,
  LeadSourceWrapper,
  SocialIconCircle,
  StatusBadge,
  StatusCircle,
  StatusWrapper,
  StyledCheckbox,
  Title,
  UserIconContainer,
} from './LeadsPool.styles';
import Actions from './components/Actions';
import SearchAndFilter from './components/SearchAndFilter';
import StatsCards from './components/StatsCards';
import { useLeadsPoolSearchData } from './useSearchData';

const LeadsPool = () => {
  const dispatch = useDispatch();
  const leads = useSelector(selectLeadsPool);
  const navigate = useNavigate();
  const totalPage = useSelector(selectLeadsPoolPagesCount);
  const loading = useSelector(selectLeadsPoolLoading);
  const totalConverted = useSelector(selectLeadsPoolTotalConverted);
  const totalValue = useSelector(selectLeadsPoolTotalOrdersSum);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const { searchData, setLeadsPoolSearchData, resetSearchData } = useLeadsPoolSearchData();
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [openTakeModal, setOpenTakeModal] = useState(false);
  const [currentLead, setCurrentLead] = useState(null);
  const [mode, setMode] = useState('assign');
  const [appointmentOrders, setAppointmentOrders] = useState([]);
  const [appointmentLead, setAppointmentLead] = useState(null);
  const [openOrderSelectModal, setOpenOrderSelectModal] = useState(false);

  const isEmpty = (value) => {
    return value === null || value === undefined || value === '';
  };

  const handleViewLead = (lead) => {
    setCurrentLead(lead);
    setOpenAssignModal(true);
  };

  const handleNavigateToLeadInfo = (lead) => {
    navigate(`/lead-info?id=${lead.id}`);
  };

  const handleBulkAssign = () => {
    setMode('assign');
    setCurrentLead(null);
    setOpenAssignModal(true);
  };

  const handleBulkReAssign = () => {
    setMode('reassign');
    setCurrentLead(null);
    setOpenAssignModal(true);
  };

  const handleTakeLead = (lead) => {
    setCurrentLead(lead || null);
    setOpenTakeModal(true);
  };

  const handleAppointment = async (lead) => {
    const leadName = lead?.companyName
      ? lead.companyName
      : `${lead?.firstName || ''} ${lead?.lastName || ''}`.trim();

    try {
      const fullLead = await dispatch(getLeadById(lead.id)).unwrap();
      const approvedOrders = (fullLead?.offers || []).flatMap((offer) =>
        (offer.orders || [])
          .filter((order) => order.status?.toLowerCase() === 'approved')
          .map((order) => ({ ...order, offerName: offer.name }))
      );

      if (approvedOrders.length === 1) {
        navigate('/project-management/appointment/create-edit', {
          state: {
            lead: {
              id: lead.id,
              leadNumber: lead.leadNumber,
              name: leadName,
              orderId: approvedOrders[0].id,
              phone: lead.phoneNumber,
            },
          },
        });
      } else if (approvedOrders.length > 1) {
        setAppointmentLead({ ...lead, name: leadName });
        setAppointmentOrders(approvedOrders);
        setOpenOrderSelectModal(true);
      } else {
        navigate('/project-management/appointment/create-edit', {
          state: {
            lead: {
              id: lead.id,
              leadNumber: lead.leadNumber,
              name: leadName,
              phone: lead.phoneNumber,
            },
          },
        });
      }
    } catch {
      navigate('/project-management/appointment/create-edit', {
        state: {
          lead: {
            id: lead.id,
            leadNumber: lead.leadNumber,
            name: leadName,
            phone: lead.phoneNumber,
          },
        },
      });
    }
  };

  const handleOrderSelect = (orderId) => {
    navigate('/project-management/appointment/create-edit', {
      state: {
        lead: {
          id: appointmentLead?.id,
          leadNumber: appointmentLead?.leadNumber,
          name: appointmentLead?.name,
          orderId,
          phone: appointmentLead?.phoneNumber,
        },
      },
    });
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads((prev) =>
      prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map((lead) => lead.id));
    }
  };

  const handleSuccess = async () => {
    dispatch(getLeadsPool(searchData));
    setSelectedLeads([]);
    resetSearchData();
  };

  const handleCloseModal = () => {
    setOpenAssignModal(false);
  };

  const handleNavigateSettings = () => {
    navigate('/settings/sales-new-settings');
  };

  const userType = localStorage.getItem('userType');
  const canAccessSettings = userType === 'Super Admin' || userType === 'General Manager';

  const columns = useMemo(
    () => [
      {
        key: 'checkbox',
        title: (
          <StyledCheckbox
            type="checkbox"
            checked={selectedLeads.length === leads.length && leads.length > 0}
            onChange={handleSelectAll}
          />
        ),
        dataIndex: 'id',
        width: '50px',
        render: (id) => (
          <StyledCheckbox
            type="checkbox"
            checked={selectedLeads.includes(id)}
            onChange={() => handleSelectLead(id)}
          />
        ),
      },
      {
        key: 'leadNumber',
        title: 'Lead ID',
        dataIndex: 'leadNumber',
        render: (id, lead) => (
          <LeadIdBadge onClick={() => handleNavigateToLeadInfo(lead)}>{id}</LeadIdBadge>
        ),
      },
      {
        key: 'fullName',
        title: 'Full Name',
        dataIndex: 'businessType',
        render: (_, lead) => {
          if (lead.businessType === 'B2B') {
            return isEmpty(lead.companyName) ? '-' : lead.companyName;
          }
          const fullName = `${lead.firstName || ''} ${lead.lastName || ''}`.trim();
          return isEmpty(fullName) ? '-' : fullName;
        },
      },
      {
        key: 'phone',
        title: 'Phone',
        dataIndex: 'phoneNumber',
        render: (phone) => (isEmpty(phone) ? '-' : phone),
      },
      {
        key: 'address',
        title: 'Address',
        dataIndex: 'city',
        render: (_, lead) => {
          // Build full address from all available parts
          const addressParts = [];
          if (lead.country?.name) addressParts.push(lead.country.name);
          if (lead.region?.name) addressParts.push(lead.region.name);
          if (lead.district?.name) addressParts.push(lead.district.name);
          if (lead.city?.name) addressParts.push(lead.city.name);
          if (lead.street?.name) addressParts.push(lead.street.name);
          if (lead.buildingHouse?.name) addressParts.push(lead.buildingHouse.name);
          if (lead.address) addressParts.push(lead.address);

          const fullAddress = addressParts.join(', ');

          if (isEmpty(fullAddress)) return '-';

          // Truncate address if longer than 30 characters
          const displayAddress =
            fullAddress.length > 30 ? fullAddress.substring(0, 30) + '...' : fullAddress;

          return (
            <Tooltip title={fullAddress} placement="top" arrow>
              <HoveredText>{displayAddress}</HoveredText>
            </Tooltip>
          );
        },
      },
      {
        key: 'source',
        title: 'Source',
        dataIndex: 'leadSource',
        render: (leadSource) => {
          if (!leadSource || isEmpty(leadSource.name)) return '-';

          return (
            <LeadSourceWrapper>
              <Tooltip title={leadSource.name} placement="top" arrow>
                <span>
                  <SocialIconCircle src={leadSource.image} />
                </span>
              </Tooltip>
            </LeadSourceWrapper>
          );
        },
      },
      {
        key: 'assignee',
        title: 'Assigned to',
        dataIndex: 'assignee',
        render: (assignee) => {
          return (
            <AssigneeWrapper>
              {assignee?.name ? (
                <>
                  <UserIconContainer>
                    <Icon src={UserSvg} />
                  </UserIconContainer>
                  {assignee?.name} {assignee?.surname || ''}
                </>
              ) : (
                '-'
              )}
            </AssigneeWrapper>
          );
        },
      },
      {
        key: 'status',
        title: 'Status',
        dataIndex: 'assigneeUuid',
        render: (assigneeUuid) => {
          return (
            <StatusWrapper>
              <StatusCircle $color={assigneeUuid ? '#15C7A7' : '#FF6900'} />
              {assigneeUuid ? 'Assigned' : 'Unassigned'}
            </StatusWrapper>
          );
        },
      },
      {
        key: 'workflowStatus',
        title: 'Workflow Status',
        dataIndex: 'workflowStatus',
        render: (workflowStatus) => {
          if (!workflowStatus || isEmpty(workflowStatus.name)) return '-';

          const statusName = workflowStatus.name;
          const displayName =
            statusName.length > 15 ? `${statusName.substring(0, 15)}...` : statusName;

          return (
            <StatusBadge $color={workflowStatus.color} $textColor="#ffffff">
              {displayName}
            </StatusBadge>
          );
        },
      },
      {
        key: 'type',
        title: 'Type',
        dataIndex: 'type',
        render: (type) => {
          if (!type || isEmpty(type)) return '-';
          return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase().replace('_', ' ');
        },
      },
      {
        key: 'timestamp',
        title: 'Timestamp',
        dataIndex: 'createdAt',
        render: (createdAt) => formatDateTime(createdAt),
      },
      {
        key: 'action',
        title: 'Action',
        dataIndex: 'id',
        render: (id, lead, index) => (
          <Actions
            id={id}
            lead={lead}
            onView={handleViewLead}
            onTake={handleTakeLead}
            onAppointment={handleAppointment}
            setMode={(e) => setMode(e)}
            dropdownPosition={leads && index >= leads.length - 2 ? 'up' : 'down'}
          />
        ),
      },
    ],
    [navigate, selectedLeads, leads]
  );

  const conversionRate =
    totalPage > 0 ? `${((totalConverted / totalPage) * 100).toFixed(1)}%` : '0%';

  useEffect(() => {
    dispatch(getLeadsPool(searchData));
  }, [searchData]);

  return (
    <Container>
      <Header>
        <Title>Leads Pool</Title>
        {canAccessSettings && (
          <Button width={50} outlined onClick={handleNavigateSettings}>
            <SettingsIcon />
          </Button>
        )}
      </Header>
      <StatsCards
        total={totalPage}
        converted={totalConverted}
        value={totalValue}
        conversionRate={conversionRate}
      />
      <SearchAndFilter
        searchData={searchData}
        setSearchData={setLeadsPoolSearchData}
        selectedLeads={leads.filter((lead) => selectedLeads.includes(lead.id))}
        onAssign={handleBulkAssign}
        onReAssign={handleBulkReAssign}
        onTakeLead={() => setOpenTakeModal(true)}
      />
      <Table
        data={leads}
        columns={columns}
        dataCount={totalPage}
        count={searchData.limit}
        loading={loading}
        totalPages={Math.ceil(totalPage / searchData.limit) || 1}
        currentPage={searchData.page}
        handleRowCountChange={(count) => setLeadsPoolSearchData({ limit: count, page: 1 })}
        editableRowCount={true}
        onPaginationChange={(page) => setLeadsPoolSearchData({ page })}
        emptyText="No leads found"
      />
      <AssignLeadModal
        leadData={currentLead ? [currentLead.id] : selectedLeads}
        isOpen={openAssignModal}
        mode={mode}
        onSuccess={handleSuccess}
        onClose={handleCloseModal}
      />
      <TakeLeadModal
        leadIds={currentLead ? [currentLead.id] : selectedLeads}
        isOpen={openTakeModal}
        onSuccess={handleSuccess}
        onClose={() => setOpenTakeModal(false)}
      />
      <OrderSelectModal
        isOpen={openOrderSelectModal}
        onClose={() => setOpenOrderSelectModal(false)}
        orders={appointmentOrders}
        onSelect={handleOrderSelect}
      />
    </Container>
  );
};

export default LeadsPool;
